import { scaleLinear } from "d3-scale";
import max from "lodash-es/max";
import min from "lodash-es/min";
import React from "react";
import { ProductClass } from "../../graphQL/types/shared";
import { generateStringProductId } from "../../graphQL/Utils";
import { newHSColorMap } from "../../Utils";
import { getIntervalWidener } from "../../viz/Utils";
import { ProductSpaceResponseDatum as FetchedDatum } from "./index";
import SectorLabel from "./SectorLabel";
import { LabelPosition } from "./Utils";

const widenInterval = getIntervalWidener(1.1);
const getScales = (
  data: FetchedDatum[],
  chartWidth: number,
  chartHeight: number
) => {
  const allXValues: number[] = [];
  const allYValues: number[] = [];
  data.forEach(({ x, y }) => {
    allXValues.push(x);
    allYValues.push(y);
  });
  const minX = min(allXValues);
  const maxX = max(allXValues);
  const minY = min(allYValues);
  const maxY = max(allYValues);
  if (
    minX === undefined ||
    maxX === undefined ||
    minY === undefined ||
    maxY === undefined
  ) {
    throw new Error("data is likely empty");
  }
  const xScale = scaleLinear<number, number>()
    .domain(widenInterval(minX, maxX))
    .range([0, chartWidth]);
  const yScale = scaleLinear<number, number>()
    .domain(widenInterval(minY, maxY))
    .range([chartHeight, 0]);

  return { xScale, yScale };
};

const textilesSectorTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 1256,
});
const agricultureSectorTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 829,
});
const stoneSectorTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 1401,
});
const mineralsTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 910,
});
const metalsTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 1498,
});
const chemicalsTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 990,
});
const vehiclesTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 1763,
});
const machineryTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 1684,
});
const electronicsTargetID = generateStringProductId({
  productClass: ProductClass.HS,
  id: 1745,
});

const allTargetIDs = [
  textilesSectorTargetID,
  agricultureSectorTargetID,
  stoneSectorTargetID,
  mineralsTargetID,
  metalsTargetID,
  chemicalsTargetID,
  vehiclesTargetID,
  machineryTargetID,
  electronicsTargetID,
];

interface Props {
  products: FetchedDatum[];
  chartWidth: number;
  chartHeight: number;
}
const SectorLabels = (props: Props) => {
  const { products, chartWidth, chartHeight } = props;
  const { xScale, yScale } = getScales(products, chartWidth, chartHeight);
  const labels = allTargetIDs.map((targetID) => {
    const foundProduct = products.find(
      (product) => product.product.id === targetID
    );
    if (foundProduct === undefined) {
      return null;
    }
    const {
      x,
      y,
      product: {
        id: productID,
        topLevelParent: { id: sectorID, shortName },
      },
    } = foundProduct;
    const color = newHSColorMap.get(sectorID);
    if (color === undefined) {
      throw new Error("Cannot find color for category ID " + sectorID);
    }
    let position: LabelPosition;
    if (
      productID === mineralsTargetID ||
      productID === chemicalsTargetID ||
      productID === agricultureSectorTargetID ||
      productID === stoneSectorTargetID ||
      productID === machineryTargetID
    ) {
      position = LabelPosition.Top;
    } else {
      position = LabelPosition.Bottom;
    }
    return (
      <SectorLabel
        position={position}
        tipX={xScale(x)}
        tipY={yScale(y)}
        chartHeight={chartHeight}
        label={shortName}
        key={`label-${sectorID}`}
        color={color}
        svgString={require("../../img/icons/categories_new/" +
          sectorID +
          ".svg")}
      />
    );
  });
  return <>{labels}</>;
};

export default SectorLabels;
